import { reactQueryClient } from '@/utils/reactQueryClient';
import { ConsultNoteStatus } from '@heidi/schema/ml-scribe';
import {
  FrontendNoteFailureReason,
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
  StreamingStatus,
} from '@heidi/session/contracts';
import { failArtifactGeneration } from '../../artifact-generation-coordinator';
import {
  finishSessionGeneration,
  isLatestGeneration,
} from '../../generation-coordinator';
import { noteGenerationLogger } from '../../noteGenerationLogger';
import {
  captureGenerationAttemptBaseline,
  type PendingFailureCommit,
} from '../recovery/attempt-outcome';

/**
 * Commit the generic terminal failure for one generation attempt: restore the
 * previous result, mark the consult note FAILED, drop to NOT_STARTED and
 * refetch the session so the backend's `failure_reason` reaches the overlay.
 * Client-held failures (`heldFeError`) keep their reason without refetching,
 * since a GET would drop the client-only `feError`.
 *
 * `finish: false` is the inline path: the body calls this inside `onError`
 * while the generation is current, and finishes the handle itself later.
 *
 * Both paths reject stale handles before any write.
 * `finish: true` is the deferred path: a recovery layer held the commit back
 * while it decided whether to retry, so by now a newer generation may own the
 * session cache and the artifact machine. In that case the stale attempt has
 * nothing left to commit; `beginSessionGeneration` already superseded its
 * handle, and writing FAILED here would clobber the newer attempt.
 */
export function commitFailedGeneration(
  pending: PendingFailureCommit,
  { finish }: { finish: boolean }
): void {
  const { sessionId, previousResult, handle, error, heldFeError } = pending;
  if (!isLatestGeneration(handle)) return;
  if (finish) failArtifactGeneration({ kind: 'note', sessionId }, error);
  reactQueryClient.setQueryData<QuerySessionResponse>(
    [GET_SESSION_BY_ID, { sessionId }],
    (oldData) => {
      if (!oldData) {
        return undefined;
      }

      return {
        ...oldData,
        abortController: new AbortController(),
        streamingStatus: StreamingStatus.NOT_STARTED,
        ...(heldFeError && { feError: heldFeError }),
        consult_note: {
          ...oldData.consult_note,
          ...(pending.baseline ? captureGenerationAttemptBaseline(pending.baseline) : {}),
          result: previousResult,
          status: ConsultNoteStatus.FAILED,
          // The pane reads `failure_reason ?? feError`, so a server reason
          // stamped by an earlier attempt would mask the held state.
          ...(heldFeError && { failure_reason: null }),
        },
      };
    }
  );
  // A refetch would replace the held FAILED state with the server's.
  if (!heldFeError) {
    void reactQueryClient.invalidateQueries({
      queryKey: [GET_SESSION_BY_ID, { sessionId }],
    });
  } else if (heldFeError === FrontendNoteFailureReason.OFFLINE) {
    noteGenerationLogger.info('onerror classified offline', { sessionId });
  }
  if (finish) finishSessionGeneration(handle);
}
